import decode from 'jwt-decode';

const TOKEN_KEY = 'eurostar_jwt';

export class AuthorizeService {
    _callbacks = [];
    _nextSubscriptionId = 0;
    _user = null;
    _isAuthenticated = false;

    async isAuthenticated() {
        const token = this.getStoredToken();
        if (!token) return false;
        try {
            const decoded = decode(token);
            return decoded.exp * 1000 > Date.now();
        } catch {
            return false;
        }
    }

    async getUser() {
        const token = this.getStoredToken();
        if (!token) return null;
        try {
            const decoded = decode(token);
            if (decoded.exp * 1000 <= Date.now()) return null;
            return {
                name: decoded.email || decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'],
                sub: decoded.sub || decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier']
            };
        } catch {
            return null;
        }
    }

    async getAccessToken() {
        const token = this.getStoredToken();
        if (!token) return null;
        try {
            const decoded = decode(token);
            if (decoded.exp * 1000 <= Date.now()) {
                this.removeToken();
                return null;
            }
            return token;
        } catch {
            this.removeToken();
            return null;
        }
    }

    async login(email, password) {
        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password })
            });

            if (response.ok) {
                const data = await response.json();
                this.storeToken(data.token);
                this.updateState(true);
                return this.success();
            } else {
                const error = await response.json();
                return this.error(error.message || 'Inloggningen misslyckades');
            }
        } catch (err) {
            return this.error('Ett fel uppstod vid inloggning');
        }
    }

    async register(email, password, confirmPassword) {
        try {
            const response = await fetch('/api/auth/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password, confirmPassword })
            });

            if (response.ok) {
                const data = await response.json();
                this.storeToken(data.token);
                this.updateState(true);
                return this.success();
            } else {
                const error = await response.json();
                return this.error(error.message || 'Registreringen misslyckades');
            }
        } catch (err) {
            return this.error('Ett fel uppstod vid registrering');
        }
    }

    async signIn(state) {
        // For compatibility with existing components that call signIn
        // Redirect to login page
        return this.redirect();
    }

    async signOut(state) {
        this.removeToken();
        this.updateState(false);
        return this.success(state);
    }

    async completeSignIn(url) {
        return this.success();
    }

    async completeSignOut(url) {
        this.removeToken();
        this.updateState(false);
        return this.success();
    }

    getStoredToken() {
        return localStorage.getItem(TOKEN_KEY);
    }

    storeToken(token) {
        localStorage.setItem(TOKEN_KEY, token);
    }

    removeToken() {
        localStorage.removeItem(TOKEN_KEY);
    }

    updateState(isAuthenticated) {
        this._isAuthenticated = isAuthenticated;
        if (isAuthenticated) {
            const token = this.getStoredToken();
            if (token) {
                try {
                    this._user = decode(token);
                } catch {
                    this._user = null;
                }
            }
        } else {
            this._user = null;
        }
        this.notifySubscribers();
    }

    subscribe(callback) {
        this._callbacks.push({ callback, subscription: this._nextSubscriptionId++ });
        return this._nextSubscriptionId - 1;
    }

    unsubscribe(subscriptionId) {
        const subscriptionIndex = this._callbacks
            .map((element, index) => element.subscription === subscriptionId ? { found: true, index } : { found: false })
            .filter(element => element.found === true);
        if (subscriptionIndex.length !== 1) {
            throw new Error(`Found an invalid number of subscriptions ${subscriptionIndex.length}`);
        }

        this._callbacks.splice(subscriptionIndex[0].index, 1);
    }

    notifySubscribers() {
        for (let i = 0; i < this._callbacks.length; i++) {
            const callback = this._callbacks[i].callback;
            callback();
        }
    }

    error(message) {
        return { status: AuthenticationResultStatus.Fail, message };
    }

    success(state) {
        return { status: AuthenticationResultStatus.Success, state };
    }

    redirect() {
        return { status: AuthenticationResultStatus.Redirect };
    }

    static get instance() { return authService }
}

const authService = new AuthorizeService();

export default authService;

export const AuthenticationResultStatus = {
    Redirect: 'redirect',
    Success: 'success',
    Fail: 'fail'
};
